"use client";

import { useState, useEffect, useCallback, type ReactNode } from "react";
import { useAtomValue } from "jotai";
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
} from "@/components/shadcn/popover";
import {
  selectedNodeIdAtom,
  graphNodesAtom,
  edgesAtom,
  nodeRegistryAtom,
} from "@/store/editor-v2";
import { useVariableOptions } from "@/components/SpotflowV2/Sidebar/VariablePicker";
import type { NodeOption } from "@/components/SpotflowV2/Sidebar/variableOptions";
import type { NodeDefinition } from "@/generated";
import type { Edge } from "@xyflow/react";
import { VariablePickerPanel } from "./VariablePickerPanel";
import type { InlineVariableInsert } from "./VariablePickerPanel";

export type { InlineVariableInsert } from "./VariablePickerPanel";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onInsert: (variable: InlineVariableInsert) => void;
  children: ReactNode;
  side?: "left" | "right" | "top" | "bottom";
  options: NodeOption[];
}

/** Every upstream variable of the selected node. Inline text editors pass
 * `inlineText` so each option carries its inline-text compatibility (File
 * acceptance comes from the node's definition); without it every option
 * reads as compatible, which only suits whole-ref cells typed per row. */
export function useUpstreamVariableOptions({
  inlineText = false,
}: { inlineText?: boolean } = {}): NodeOption[] {
  const selectedNodeId = useAtomValue(selectedNodeIdAtom);
  const nodes = useAtomValue(graphNodesAtom);
  const edges = useAtomValue(edgesAtom) as Edge[];
  const nodeRegistry = useAtomValue(nodeRegistryAtom) as NodeDefinition[];
  const allOptions = useVariableOptions(
    nodes,
    selectedNodeId ?? "",
    edges,
    nodeRegistry,
    undefined,
    undefined,
    undefined,
    inlineText
  );
  if (!selectedNodeId) return [];
  return allOptions;
}

export function InlineVariablePicker({
  open,
  onOpenChange,
  onInsert,
  children,
  side = "left",
  options,
}: Props) {
  const [search, setSearch] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    if (open) {
      setSearch("");
      setActiveIndex(0);
    }
  }, [open]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onOpenChange(false);
      }
    },
    [onOpenChange]
  );

  if (options.length === 0) return <>{children}</>;

  return (
    <Popover open={open} onOpenChange={onOpenChange} modal={false}>
      <PopoverAnchor asChild>{children}</PopoverAnchor>
      <PopoverContent
        side={side}
        align="start"
        sideOffset={72}
        avoidCollisions={false}
        collisionPadding={0}
        onOpenAutoFocus={(e) => e.preventDefault()}
        onCloseAutoFocus={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
        onMouseDown={(e) => {
          if (!(e.target instanceof HTMLInputElement)) e.preventDefault();
        }}
        className="w-[330px] p-0 flex flex-col overflow-hidden"
      >
        <VariablePickerPanel
          options={options}
          search={search}
          onSearchChange={setSearch}
          activeIndex={activeIndex}
          onActiveIndexChange={setActiveIndex}
          onSelect={onInsert}
          onKeyDown={handleKeyDown}
        />
      </PopoverContent>
    </Popover>
  );
}
